// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE-BROWSER — state.read rpc handler (ABB-S6.2, SM-8)
// ═══════════════════════════════════════════════════════════════════════════
//
// The `state.read` rpc handler: parse `{route, query, cursor}` → take ONE synchronous
// committed-snapshot ref ([H2], no await mid-projection) → bind the read to the in-bridge
// `documentEpochCache` belief (1A, S6.4) → delegate to the canonical `projectStateRead`
// seam → reply `rpc-result{result: StateReadPage}` (the companion maps any `validationCode`
// to §8.A) OR `rpc-error{code ∈ cursor-stale}` (the revision-mismatch reject the browser
// detects before projecting).
//
// THE INVARIANTS (design §3.2 / SM-8):
//   • `audit` route is COMPANION-served (INV-11) — NEVER received → explicit reject.
//   • a paginated page ALWAYS carries a POPULATED `routeRevisions` tuple (the companion
//     guard `stateRoute.ts:288-294` rejects undefined/null; the projector guarantees a
//     route-specific tuple, but the handler ASSERTS it — a body that ever omits it is a
//     bug the handler turns into an explicit reject, never a silent empty-revision cursor).
//   • the 7 unimplemented routes → `validationCode='route-not-yet-projected'` (the seam
//     fallthrough), NEVER a fake empty page.
//   • cursor-stale: the cursor's binding `routeRevisions` ≠ the live tuple → reject (a
//     mid-paging mutation invalidated the cursor); the companion ALSO checks epoch/document
//     mismatch (`stateRoute.ts:302-324`) — this is the browser's revision-tuple check.
//   • document-belief binding (1A): the read binds to the cached documentEpoch belief; if
//     the companion re-keyed the document (a swap hydrated into the cache) AFTER the
//     snapshot the handler captured, the snapshot's `documentEpoch` ≠ the cached belief →
//     the read is stale → `cursor-stale` (a fresh re-read picks up the new snapshot). This
//     closes the project-OUT ⇔ hydrate-IN symmetry: the read consumes the SAME belief the
//     adoption path (S3 welcome/session-changed) hydrates.
//
// DEP-GRAPH (ПРИОРИТЕТ #2): imports ONLY sibling agentBridge leaves + the pure
// `ai/sceneSummaryProjector` (a store-free projection fn). EVERY scene coupling (the
// committed snapshot ref) arrives via the `StateReadHandlerDeps` DI object — the module
// imports NO editorStore. `npm run deps:graph` cycles=0.
// ═══════════════════════════════════════════════════════════════════════════

import {
  type DecodedProjectionCursor,
  MAX_PROJECTION_LIMIT,
  projectStateRead,
  type SceneSnapshotRef,
  type StateReadPage,
  type StateReadQuery,
} from "../ai/sceneSummaryProjector";
import type { DocumentEpochBelief, DocumentEpochCache } from "./documentEpochCache";
import type { RpcHandler, RpcHandlerContext, RpcHandlerOutcome } from "./clientTypes";

/**
 * The DI surface for the state.read handler. EVERY editor coupling is here — the module
 * imports no editorStore. S7 supplies these from the live store; tests inject mocks.
 */
export interface StateReadHandlerDeps {
  /**
   * Capture ONE synchronous committed-snapshot ref ([H2]) — read the live store ONCE,
   * synchronously, BEFORE the projection (no await mid-projection). S7 supplies a closure
   * that maps the live `geometryDocument` + selection + ABB-S1 revisions onto the
   * `SceneSnapshotRef` shape. Called once per `state.read` rpc.
   */
  captureSnapshot: () => SceneSnapshotRef;
  /**
   * The in-bridge documentEpoch belief cache (1A, S6.4). The read binds to `cache.get()`:
   * the captured snapshot's `documentEpoch` must match the cached companion belief, else the
   * document was re-keyed after the snapshot (a remote/late swap hydrated in) → cursor-stale.
   * Supplied by S7 (the SAME cache the adoption path hydrates).
   */
  documentEpochCache: DocumentEpochCache;
}

/**
 * The narrowed `state.read` rpc payload (the companion forwarded it opaquely, INV-1). The
 * handler narrows the SHAPE only — `route` / `query` values stay browser-adjudicated.
 */
interface StateReadPayload {
  readonly route: string;
  readonly query: StateReadQuery;
  readonly cursor: DecodedProjectionCursor | null;
}

/** A record-or-null narrow (the wire is untrusted — a non-object payload is malformed). */
function asRecord(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;
}

/**
 * Parse + clamp the forwarded `?limit` to an integer in `[1, MAX_PROJECTION_LIMIT]`, or
 * `undefined` (→ the projector applies its default page size) for a non-finite /
 * non-integer / `< 1` value. The companion builds `payload.query` from `URLSearchParams`, so `limit`
 * arrives as a STRING (`query.limit = String(limit)`); a numeric value is also accepted
 * defensively (a future caller, a test). Mirrors the companion's `clampStateLimit`
 * (`readBudget.ts`) — the SINGLE clamp rule (`Number(raw)`; non-finite/non-integer/`<1` →
 * default; over-max → capped) so the browser + companion never drift on the page size.
 * Returning `undefined` (vs the default 200) lets the projector own the absent→default
 * fallback in ONE place (`clampLimit`); we only forward a VALID parsed integer here.
 */
function parseLimit(raw: unknown): number | undefined {
  if (typeof raw !== "string" && typeof raw !== "number") {
    return undefined; // absent / wrong shape → projector applies the default.
  }
  const n = typeof raw === "number" ? raw : Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 1) {
    return undefined; // ''/'abc'/'0'/'-1'/'2.5'/NaN → default (mirrors clampStateLimit).
  }
  return Math.min(n, MAX_PROJECTION_LIMIT);
}

/**
 * Parse the forwarded `?fields` into the `string[]` projection allowlist, or `undefined` (the
 * projector applies no field filter). The companion forwards it as a STRING comma-list
 * (`query.fields = value`, stateRoute.ts:604) and binds it into the cursor by SPLITTING on `,`
 * + trimming empties (`deriveQueryShape`:653-657) — the handler mirrors that split so the SAME
 * `['color','layer']` allowlist the cursor binds reaches the projector. A pre-split `string[]`
 * is also accepted defensively (every member must be a string). An empty / whitespace-only
 * string → no members → `undefined` (no field filter). Members are trimmed; empties dropped
 * (mirrors the companion). NOT sorted here — the projector reads the set, not the order.
 */
function parseFields(raw: unknown): readonly string[] | undefined {
  if (Array.isArray(raw)) {
    return raw.every((f) => typeof f === "string") ? (raw as readonly string[]) : undefined;
  }
  if (typeof raw !== "string") {
    return undefined; // absent / wrong shape → no field filter.
  }
  const members = raw
    .split(",")
    .map((f) => f.trim())
    .filter((f) => f.length > 0);
  return members.length > 0 ? members : undefined;
}

/**
 * Parse a forwarded boolean query option (e.g. `?includeReferenceCount`) from its STRING form,
 * or `undefined` (absent / wrong shape → the projector applies the default). The companion
 * forwards URLSearchParams values as STRINGS (`query[key] = value`, stateRoute.ts:604), so a
 * boolean param arrives as a string. Returns `true` for the documented truthy forms — BOTH
 * `'true'` AND the numeric `'1'` form (the docs spell `?includeReferenceCount=1`,
 * stateRoute.ts:96 / fullstack:964 — a `=1` numeric flag is the canonical query form, while a
 * caller / a test may also pass `=true`). Any OTHER string (`'false'` / `'0'` / `''` / a typo)
 * → `false` (the conservative default). A real boolean is accepted defensively (a future
 * caller / a test).
 */
function parseBoolean(raw: unknown): boolean | undefined {
  if (typeof raw === "boolean") {
    return raw; // accepted defensively (a future caller / a test passes a real boolean).
  }
  if (typeof raw !== "string") {
    return undefined; // absent / wrong shape → the projector applies the default.
  }
  return raw === "true" || raw === "1"; // 'true'/'1' → true; any other string → false.
}

/**
 * Parse a forwarded numeric query value (`?osnapX=`/`?osnapY=`/`?osnapRadius=`, T8) to a
 * finite number, or `undefined` for an absent/non-finite/non-numeric value. The companion
 * forwards URLSearchParams values as STRINGS; a numeric value is also accepted defensively
 * (a future caller, a test). Positivity (`osnapRadius > 0`) is NOT enforced here — that is
 * `osnapQuery.ts`'s own route-specific reject (this seam only narrows SHAPE, mirroring the
 * established seam/body split — e.g. `blocks`'s sort-requires-flag gate lives in the body,
 * not here).
 *
 * A blank/whitespace-only string is rejected BEFORE the `Number()` conversion: `Number("")`
 * is `0` (and `Number.isFinite(0)` is `true`) in JavaScript, so without this guard a malformed
 * `?osnapX=` (e.g. a client bug that stringifies `undefined` as `""`) would silently parse to
 * the origin (a DIFFERENT valid coordinate) instead of leaving `osnapX` absent — a fail-open
 * hole that violates this route's documented fail-closed contract (a malformed coordinate must
 * never silently become a different real query, CLAUDE.md ПРИОРИТЕТ #1).
 */
function parseFiniteNumber(raw: unknown): number | undefined {
  if (typeof raw === "string" && raw.trim().length === 0) {
    return undefined; // a blank/whitespace-only string is NOT a valid number — never silently → 0.
  }
  if (typeof raw !== "string" && typeof raw !== "number") {
    return undefined; // absent / wrong shape → the route body's own reject applies.
  }
  const n = typeof raw === "number" ? raw : Number(raw);
  return Number.isFinite(n) ? n : undefined; // non-finite ('abc', NaN, ±Infinity) → absent.
}

/**
 * The accepted `?consistency` vocabulary (conn-spec §5.5 [M4]): `lenient` relaxes the
 * route-revision staleness check on a continuation; `strict` (the default) enforces it. An
 * out-of-vocabulary value is NORMALIZED to `'strict'` — NEVER forwarded opaquely (an unknown
 * consistency must not be interpreted as lenient by accident, the fail-safe direction).
 */
const CONSISTENCY_VALUES = new Set<string>(["lenient", "strict"]);

/**
 * Normalize a forwarded `?consistency` value to the documented vocabulary, or `undefined`
 * (absent → the projector/handler applies the `strict` default). An in-vocabulary string
 * (`'lenient'`/`'strict'`) passes through; ANY out-of-vocabulary string (`'loose'`, a typo)
 * is normalized to `'strict'` (the fail-SAFE direction — an unknown consistency must never be
 * silently treated as lenient). A non-string is `undefined` (the default). Mirrors the
 * companion's CONTROL-key handling (stateRoute.ts:174): consistency adjusts HOW staleness is
 * judged; the browser is the adjudicator (INV-1), so it must not forward an unknown value.
 */
function parseConsistency(raw: unknown): "lenient" | "strict" | undefined {
  if (typeof raw !== "string") {
    return undefined; // absent / wrong shape → the strict default applies.
  }
  return CONSISTENCY_VALUES.has(raw) ? (raw as "lenient" | "strict") : "strict";
}

/**
 * Narrow the opaque `query` to the documented `StateReadQuery` shape (values stay opaque /
 * browser-adjudicated — the projector rejects bad values). Unknown keys are ignored (the
 * companion already `400 unknown-field`s a stray top-level query KEY before forwarding).
 */
function narrowQuery(raw: unknown): StateReadQuery {
  const rec = asRecord(raw);
  if (rec === null) {
    return {};
  }
  const query: {
    scope?: string;
    limit?: number;
    fields?: readonly string[];
    sort?: string;
    viewportEntityId?: string;
    includeReferenceCount?: boolean;
    fullField?: string;
    consistency?: string;
    osnapX?: number;
    osnapY?: number;
    osnapRadius?: number;
  } = {};
  if (typeof rec.scope === "string") query.scope = rec.scope;
  // `limit` arrives as a STRING from the companion's URLSearchParams (`String(limit)`); parse
  // + clamp it (mirrors `clampStateLimit`). A number is also accepted defensively. An invalid
  // value is dropped → the projector's DEFAULT_PROJECTION_LIMIT applies.
  const parsedLimit = parseLimit(rec.limit);
  if (parsedLimit !== undefined) query.limit = parsedLimit;
  // `fields` arrives as a STRING comma-list from the companion (`query.fields = value`,
  // stateRoute.ts:604 — the companion derives + binds `fields=` into the cursor by SPLITTING
  // the comma-list, deriveQueryShape:653). The handler MUST split the SAME way, else a string
  // `'color,layer'` never reaches the projector as the `['color','layer']` allowlist (it was
  // dropped entirely — the array-only guard missed the documented string form). A pre-split
  // array is also accepted defensively (a future caller / a test).
  // `fields` arrives as a STRING comma-list from the companion (`query.fields = value`,
  // stateRoute.ts:604 — the companion derives + binds `fields=` into the cursor by SPLITTING
  // the comma-list, deriveQueryShape:653). The handler MUST split the SAME way, else a string
  // `'color,layer'` never reaches the projector as the `['color','layer']` allowlist (it was
  // dropped entirely — the array-only guard missed the documented string form). A pre-split
  // array is also accepted defensively (a future caller / a test).
  const parsedFields = parseFields(rec.fields);
  if (parsedFields !== undefined) query.fields = parsedFields;
  if (typeof rec.sort === "string") query.sort = rec.sort;
  if (typeof rec.viewportEntityId === "string") query.viewportEntityId = rec.viewportEntityId;
  // `includeReferenceCount` arrives as a STRING ('true'/'false') from the companion's
  // URLSearchParams (`query.includeReferenceCount = value`, stateRoute.ts:604). Parse it:
  // 'true' → true, any other string → false. A boolean is also accepted defensively. Without
  // this the documented `?includeReferenceCount=true` never reached the projector (the
  // boolean-only guard dropped the string form) → the reference-count column was silently
  // omitted (`blocks?sort=count` requires it).
  // `includeReferenceCount` arrives as a STRING ('true'/'false') from the companion's
  // URLSearchParams (`query.includeReferenceCount = value`, stateRoute.ts:604). Parse it:
  // 'true' → true, any other string → false. A boolean is also accepted defensively. Without
  // this the documented `?includeReferenceCount=true` never reached the projector (the
  // boolean-only guard dropped the string form) → the reference-count column was silently
  // omitted (`blocks?sort=count` requires it).
  const parsedIncludeRefCount = parseBoolean(rec.includeReferenceCount);
  if (parsedIncludeRefCount !== undefined) query.includeReferenceCount = parsedIncludeRefCount;
  if (typeof rec.fullField === "string") query.fullField = rec.fullField;
  // `consistency` is the cursor-staleness STRICTNESS selector (conn-spec §5.5 [M4]). Normalize
  // it to the documented vocabulary — an out-of-vocabulary value is forced to 'strict' (the
  // fail-safe), NEVER forwarded opaquely as an unknown consistency (which a downstream check
  // might mis-read as lenient).
  const parsedConsistency = parseConsistency(rec.consistency);
  if (parsedConsistency !== undefined) query.consistency = parsedConsistency;
  // T8 `osnap-query` — the world-space query point + search radius (all forwarded as STRINGS
  // by the companion's URLSearchParams; a numeric value is also accepted defensively). Absent/
  // non-finite → left undefined (the route body's own `invalid-osnap-query` reject applies).
  const parsedOsnapX = parseFiniteNumber(rec.osnapX);
  if (parsedOsnapX !== undefined) query.osnapX = parsedOsnapX;
  const parsedOsnapY = parseFiniteNumber(rec.osnapY);
  if (parsedOsnapY !== undefined) query.osnapY = parsedOsnapY;
  const parsedOsnapRadius = parseFiniteNumber(rec.osnapRadius);
  if (parsedOsnapRadius !== undefined) query.osnapRadius = parsedOsnapRadius;
  return query;
}

/**
 * Narrow the opaque decoded `cursor` payload to `DecodedProjectionCursor`, or null (the
 * FIRST page, no cursor). The companion relays the DECODED cursorPayload (post-HMAC-verify)
 * — `{offsetKey, routeRevisions, resolvedSpaceId, projectionVersion, ...}`. The handler
 * reads `offsetKey` + `routeRevisions` AND preserves the `resolvedSpaceId` +
 * `projectionVersion` the companion SIGNED into the envelope (`cursorKeyEnvelope.ts:67`/
 * `:73`) so the staleness check can CROSS-CHECK them against the live page (an active-space
 * switch / a reader-schema change mid-pagination → `cursor-stale`). DROPPING them (the prior
 * bug) defeated those two staleness signals entirely — the companion bound them but the
 * browser never compared them. A non-string/null `resolvedSpaceId` or non-number
 * `projectionVersion` is left `undefined` (the cross-check skips an absent member).
 */
function narrowCursor(raw: unknown): DecodedProjectionCursor | null {
  const rec = asRecord(raw);
  if (rec === null) {
    return null;
  }
  const routeRevisions: Record<string, number> = {};
  const rawRevisions = asRecord(rec.routeRevisions);
  if (rawRevisions !== null) {
    for (const [k, v] of Object.entries(rawRevisions)) {
      if (typeof v === "number") {
        routeRevisions[k] = v;
      }
    }
  }
  const cursor: {
    offsetKey: unknown;
    routeRevisions: Record<string, number>;
    resolvedSpaceId?: string | null;
    projectionVersion?: number;
  } = { offsetKey: rec.offsetKey, routeRevisions };
  // Preserve the companion-signed cursor-bound members for the cross-check (STEP 3). The
  // companion signs `resolvedSpaceId: string | null` + `projectionVersion: integer`
  // (cursorKeyEnvelope.ts) — accept exactly those kinds; anything else is left absent.
  if (rec.resolvedSpaceId === null || typeof rec.resolvedSpaceId === "string") {
    cursor.resolvedSpaceId = rec.resolvedSpaceId;
  }
  if (typeof rec.projectionVersion === "number" && Number.isInteger(rec.projectionVersion)) {
    cursor.projectionVersion = rec.projectionVersion;
  }
  return cursor;
}

/** Parse the rpc payload into the narrowed `{route, query, cursor}`, or null if malformed. */
function parsePayload(payload: unknown): StateReadPayload | null {
  const rec = asRecord(payload);
  if (rec === null || typeof rec.route !== "string") {
    return null;
  }
  return {
    route: rec.route,
    query: narrowQuery(rec.query),
    cursor: narrowCursor(rec.cursor),
  };
}

/** An rpc-error outcome (the companion maps the code via `mapBrowserCodeToHttp`). */
function reject(code: string, message: string, detail?: unknown): RpcHandlerOutcome {
  return { ok: false, code, message, ...(detail !== undefined ? { detail } : {}) };
}

/**
 * The reason a continuation cursor is STALE (`null` = consistent). Distinguishes the
 * `resolvedSpaceId`/`projectionVersion` cross-checks (STEP 3) from the route-revision
 * comparison (the one a `consistency=lenient` continuation relaxes, STEP 4) — so the
 * lenient branch can skip ONLY the revision reason while still enforcing the others.
 */
type CursorStaleReason = "resolved-space-changed" | "projection-version-changed" | "revision-changed";

/**
 * Whether the cursor is CONSISTENT with the page the projector just minted, and WHY it is
 * stale if not. A cursor carries the binding members of the page it was minted on; a
 * mid-paging change in ANY of them is staleness:
 *   • `resolvedSpaceId` (STEP 3) — an active-space switch mid-pagination re-resolves
 *     `?scope=active-space` to a DIFFERENT space → the cursor pages a stale space →
 *     `resolved-space-changed`. The companion SIGNS `resolvedSpaceId` into the envelope
 *     (`cursorKeyEnvelope.ts:67`) and relays it; the browser compares it to the live page's
 *     `resolvedSpaceId`. Skipped when the cursor omitted it (defensive) or the page omits it.
 *   • `projectionVersion` (STEP 3) — a browser reader column-schema change between pages
 *     invalidates an old cursor (`cursorKeyEnvelope.ts:73`) → `projection-version-changed`.
 *   • `routeRevisions` (the [L7] tuple) — a bound revision moved (a mid-paging mutation that
 *     changed THIS route's data) → `revision-changed`. Compared only for the keys the cursor
 *     carried (a superset on the new page is fine).
 * The FIRST page (no cursor) is always consistent. These cross-checks are the SHARED
 * one-truth the 7 SCENE-QUERY successors inherit (they reuse this predicate unchanged).
 */
function cursorStaleReason(
  cursor: DecodedProjectionCursor | null,
  page: StateReadPage,
): CursorStaleReason | null {
  if (cursor === null) {
    return null; // first page — no prior binding to violate.
  }
  // STEP 3 — resolvedSpaceId cross-check (an active-space switch mid-pagination). Both sides
  // must be present to compare; the page always carries `resolvedSpaceId` (incl. `null` for a
  // document/whole-doc route), so a mismatch is a real active-space switch.
  if (
    cursor.resolvedSpaceId !== undefined &&
    page.resolvedSpaceId !== undefined &&
    cursor.resolvedSpaceId !== page.resolvedSpaceId
  ) {
    return "resolved-space-changed";
  }
  // STEP 3 — projectionVersion cross-check (a browser reader-schema change between pages).
  if (
    cursor.projectionVersion !== undefined &&
    page.projectionVersion !== undefined &&
    cursor.projectionVersion !== page.projectionVersion
  ) {
    return "projection-version-changed";
  }
  // [L7] route-revision comparison — the reason a lenient continuation relaxes (STEP 4).
  const live = page.routeRevisions;
  if (live === undefined) {
    return null; // a reject page has no binding to compare (handled separately).
  }
  for (const [key, value] of Object.entries(cursor.routeRevisions)) {
    const current = live[key];
    if (current !== undefined && current !== value) {
      return "revision-changed"; // a bound revision moved between pages → stale.
    }
  }
  return null;
}

/**
 * Whether a continuation cursor is STALE, honoring the `consistency` strictness (STEP 4,
 * conn-spec §5.5 [M4]). STRICT (the default) rejects on ANY stale reason. LENIENT relaxes
 * ONLY the `revision-changed` reason (it tolerates an unrelated route-revision bump on a
 * continuation) — it STILL rejects on `resolved-space-changed` / `projection-version-changed`
 * (an active-space switch or a reader-schema change is a genuine cursor break that lenient
 * must NOT swallow), AND the epoch/documentId/query-shape guards upstream (the companion's
 * envelope check + the handler's `documentBeliefStale`) are UNAFFECTED — lenient narrows only
 * this revision comparison, nothing else. This is the SHARED predicate the 7 successors reuse.
 */
function isStale(
  cursor: DecodedProjectionCursor | null,
  page: StateReadPage,
  consistency: string | undefined,
): boolean {
  const reason = cursorStaleReason(cursor, page);
  if (reason === null) {
    return false;
  }
  // LENIENT relaxes ONLY the route-revision reason; every other reason still stales. The
  // handler's `narrowQuery` already normalized `consistency` to the documented vocabulary
  // ('lenient'/'strict'), so a literal `=== "lenient"` check is exhaustive (an unknown value
  // was normalized to 'strict' upstream → it never reaches here as a stray literal).
  if (consistency === "lenient" && reason === "revision-changed") {
    return false;
  }
  return true;
}

/**
 * Whether the captured snapshot's document belief still matches the cached companion
 * belief (1A). The read binds to the cache: if the companion re-keyed the document (a swap
 * the adoption path hydrated into the cache) AFTER the handler captured its snapshot, the
 * snapshot is for the OLD document → the read is stale.
 *
 * A document swap is a `documentId` CHANGE *or* a `documentEpoch` increase — so the binding
 * compares BOTH (#377 P2 round-3): an epoch-only check accepted a STALE snapshot when a
 * SAME-EPOCH adoption re-keyed the document to a DIFFERENT id (e.g. belief `{id:'B',epoch:5}`
 * vs snapshot `{id:'A',epoch:5}` — same epoch, different document → the read must be
 * `cursor-stale`, not served from the old document A). A null cached belief (not paired) is
 * treated as consistent (defensive): the rpc already passed the epoch-drop gate, and a
 * null-id belief carries no identity to compare against.
 */
function documentBeliefStale(
  snapshot: SceneSnapshotRef,
  belief: DocumentEpochBelief,
): boolean {
  // No cached belief yet (defensive, not paired) → do not block the read. Both the epoch and
  // the id are absent in this case, so there is nothing authoritative to compare against.
  if (belief.documentEpoch === null && belief.documentId === null) {
    return false;
  }
  // The snapshot pins the document IDENTITY + epoch it was captured from (ABB-S1
  // `documentId`/`documentEpoch`; S7 supplies both on the snapshot). Compare BOTH to the
  // cached companion belief: a mismatch on EITHER means a swap re-keyed the document between
  // capture and projection (a same-epoch id-swap is a stale snapshot the epoch alone misses).
  // A null cached member is only authoritative when the OTHER member is set: if the belief
  // has an id but no epoch (epoch-less doc), an id mismatch alone is staleness, and vice versa.
  if (belief.documentId !== null && snapshot.documentId !== belief.documentId) {
    return true; // a same-epoch (or any) DIFFERENT-id adoption re-keyed the document → stale.
  }
  if (belief.documentEpoch !== null && snapshot.documentEpoch !== belief.documentEpoch) {
    return true; // the epoch advanced past the captured snapshot → stale.
  }
  return false;
}

/**
 * Build the `state.read` rpc handler (ABB-S6.2). The returned `RpcHandler` is wired into
 * `AgentBridgeClientDeps.handlers["state.read"]` by S7. INERT until invoked (a factory).
 */
export function createStateReadHandler(deps: StateReadHandlerDeps): RpcHandler {
  return (ctx: RpcHandlerContext): RpcHandlerOutcome => {
    const parsed = parsePayload(ctx.payload);
    if (parsed === null) {
      return reject("unknown-field", "The state.read payload is missing a string route");
    }
    // INV-11: the `audit` route is COMPANION-served and is NEVER forwarded to the browser.
    // If it is ever seen (a forged / mis-routed frame), reject explicitly — never project it.
    if (parsed.route === "audit") {
      return reject(
        "unknown-field",
        "The audit route is companion-served and is never projected by the browser (INV-11)",
      );
    }

    // [H2] ONE synchronous committed-snapshot ref — captured up front, no await below.
    const snapshot = deps.captureSnapshot();

    // 1A document-belief binding: the read binds to the cached companion documentEpoch
    // belief. A swap that re-keyed the document after this snapshot was captured → stale.
    const belief = deps.documentEpochCache.get();
    if (documentBeliefStale(snapshot, belief)) {
      return reject(
        "cursor-stale",
        "The document was re-keyed after the read snapshot was captured; re-read state",
        { reason: "document-belief-changed" },
      );
    }

    // Delegate to the canonical projector (resolves ?scope once, mints a populated
    // routeRevisions tuple, returns a success page OR a validationCode reject page).
    const page = projectStateRead(parsed.route, parsed.query, parsed.cursor, snapshot);

    // cursor-stale: a bound member (routeRevisions / resolvedSpaceId / projectionVersion)
    // changed between the cursor's page and this one → reject on the rpc-error channel (the
    // browser-detected staleness; the companion ALSO checks epoch/document/query-shape on the
    // cursor envelope). STEP 4: a `consistency=lenient` continuation relaxes ONLY the
    // route-revision comparison (it still rejects an active-space switch / a reader-schema
    // change / the upstream epoch+document guards). STRICT (the default) rejects on any change.
    if (isStale(parsed.cursor, page, parsed.query.consistency)) {
      return reject(
        "cursor-stale",
        "A bound member changed between pages; the cursor is stale, re-read state",
        {
          reason: "cursor-binding-changed",
          routeRevisions: page.routeRevisions,
          resolvedSpaceId: page.resolvedSpaceId,
          projectionVersion: page.projectionVersion,
        },
      );
    }

    // A SUCCESS page (no validationCode) MUST carry a populated routeRevisions tuple (the
    // companion guard rejects undefined/null/empty-cursor; the projector guarantees it, but
    // ASSERT here — a body that ever omits it is a bug, turned into an explicit reject, not a
    // silent empty-revision cursor relayed to the companion).
    if (page.validationCode === undefined) {
      const tuple = page.routeRevisions;
      if (tuple === undefined || Object.keys(tuple).length === 0) {
        return reject(
          "browser-dispatch-error",
          `The ${parsed.route} projection returned a page with no routeRevisions tuple`,
        );
      }
    }

    // Reply rpc-result with the page (the companion relays items opaquely + maps any
    // validationCode — incl. route-not-yet-projected for the 7 unimplemented routes — to
    // §8.A). A reject page (validationCode set) STILL rides as an rpc-result: the companion
    // owns the validationCode → HTTP mapping (`stateRoute.ts:426-435`).
    return { ok: true, result: page };
  };
}
